import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { Stripe } from '@stripe/stripe-js';
import type { PaymentConfigResponse, PaymentMethodResponse } from '@lds-efsp/shared-contracts';
import { useBrand } from '../../../app/config/brand';
import { useApi, API_BASE_URL } from '../../../api/useApi';
import { Card, PageTitle, SectionTitle, InfoBanner, Button } from '../../../shared/components/ui';
import { getStripePromise } from '../lib/stripe';
import AddCardModal from '../components/AddCardModal';

const brandColor = (brand: string): { bg: string; color: string } => {
  switch (brand.toLowerCase()) {
    case 'visa':
      return { bg: '#1A1F71', color: '#FFFFFF' };
    case 'amex':
    case 'american express':
      return { bg: '#1C75BC', color: '#FFFFFF' };
    case 'mastercard':
      return { bg: '#EB001B', color: '#FFFFFF' };
    case 'discover':
      return { bg: '#F76B1C', color: '#FFFFFF' };
    default:
      return { bg: '#374151', color: '#FFFFFF' };
  }
};

const cardLabel = (m: PaymentMethodResponse): string => {
  const brand = m.cardBrand ? m.cardBrand.charAt(0).toUpperCase() + m.cardBrand.slice(1) : 'Card';
  return `${brand} ending in ${m.cardLast4 ?? '••••'}`;
};

const expLabel = (m: PaymentMethodResponse): string => {
  if (m.cardExpMonth == null || m.cardExpYear == null) return '';
  const mm = String(m.cardExpMonth).padStart(2, '0');
  const yy = String(m.cardExpYear).slice(-2);
  return `${m.isExpired ? 'Expired' : 'Expires'} ${mm}/${yy}`;
};

const PaymentMethodsPage: React.FC = () => {
  const brand = useBrand();
  const { get, post, del } = useApi();
  const [config, setConfig] = useState<PaymentConfigResponse | null>(null);
  const [methods, setMethods] = useState<PaymentMethodResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const cfg = await get<PaymentConfigResponse>(`${API_BASE_URL}/api/payments/config`);
      setConfig(cfg);
      if (cfg.configured) {
        const res = await get<{ data: PaymentMethodResponse[] }>(`${API_BASE_URL}/api/payments/methods`);
        setMethods(res.data);
      }
    } catch (err) {
      setError((err as Error).message || 'Failed to load payment methods.');
    } finally {
      setLoading(false);
    }
  }, [get]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO(deps-2026-07): newly detected by react-hooks bump, burn-down separately
    loadAll();
  }, [loadAll]);

  const stripePromise: Promise<Stripe | null> | null = useMemo(
    () => (config?.configured && config.publishableKey ? getStripePromise(config.publishableKey) : null),
    [config],
  );

  const handleSetDefault = async (id: string) => {
    setBusyId(id);
    try {
      await post(`${API_BASE_URL}/api/payments/methods/${id}/default`);
      await loadAll();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async (id: string) => {
    if (!window.confirm('Remove this card? This cannot be undone.')) return;
    setBusyId(id);
    try {
      await del(`${API_BASE_URL}/api/payments/methods/${id}`);
      await loadAll();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const cards = methods.filter((m) => m.type === 'card');

  return (
    <div>
      <PageTitle subtitle="Cards on file for court fees and service charges.">Payment methods</PageTitle>

      {config && !config.configured && (
        <div className="mb-4">
          <InfoBanner variant="warning">
            Payments aren’t enabled in this environment yet. Once Stripe keys are configured you’ll be
            able to add and manage cards here.
          </InfoBanner>
        </div>
      )}

      {error && (
        <div className="mb-4">
          <InfoBanner variant="error">{error}</InfoBanner>
        </div>
      )}

      {config?.configured && (
        <div className="mb-4">
          <InfoBanner>
            Card details are processed and stored securely by Stripe — we never see or store your full
            card number.
          </InfoBanner>
        </div>
      )}

      <Card>
        <div className="flex items-center justify-between mb-4">
          <SectionTitle>Credit &amp; debit cards</SectionTitle>
          {config?.configured && (
            <Button size="sm" onClick={() => setModalOpen(true)}>
              + Add card
            </Button>
          )}
        </div>

        {loading ? (
          <p className="text-sm" style={{ color: brand.colors.textLight }}>
            Loading…
          </p>
        ) : cards.length === 0 ? (
          <p className="text-sm" style={{ color: brand.colors.textLight }}>
            {config?.configured ? 'No cards on file yet.' : 'Payment methods will appear here.'}
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {cards.map((m) => {
              const logo = brandColor(m.cardBrand ?? '');
              const isBusy = busyId === m.id;
              return (
                <div
                  key={m.id}
                  className="flex items-center gap-4 rounded-xl p-4 border"
                  style={{
                    borderColor: m.isDefault ? brand.colors.primary : brand.colors.border,
                    boxShadow: m.isDefault ? `0 0 0 2px ${brand.colors.primaryLight}` : 'none',
                  }}
                >
                  <div
                    className="grid place-items-center rounded-md text-xs font-extrabold tracking-wide shrink-0"
                    style={{ width: 56, height: 36, background: logo.bg, color: logo.color }}
                  >
                    {(m.cardBrand ?? 'card').slice(0, 4).toUpperCase()}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm flex items-center gap-2" style={{ color: brand.colors.text }}>
                      {cardLabel(m)}
                      {m.isDefault && (
                        <span
                          className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide"
                          style={{ background: brand.colors.success, color: '#FFFFFF' }}
                        >
                          Default
                        </span>
                      )}
                      {m.isExpired && (
                        <span
                          className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide"
                          style={{ background: '#FEE2E2', color: '#991B1B' }}
                        >
                          Expired
                        </span>
                      )}
                    </div>
                    <div className="text-xs" style={{ color: brand.colors.textLight }}>
                      {[m.billingName, expLabel(m)].filter(Boolean).join(' · ')}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 shrink-0">
                    {!m.isDefault && !m.isExpired && (
                      <button
                        type="button"
                        onClick={() => handleSetDefault(m.id)}
                        disabled={isBusy}
                        className="text-xs font-semibold"
                        style={{ color: brand.colors.link }}
                      >
                        Set default
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleRemove(m.id)}
                      disabled={isBusy}
                      className="text-xs font-semibold"
                      style={{ color: brand.colors.error }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {modalOpen && stripePromise && (
        <AddCardModal
          stripePromise={stripePromise}
          onClose={() => setModalOpen(false)}
          onAdded={() => {
            setModalOpen(false);
            loadAll();
          }}
        />
      )}
    </div>
  );
};

export default PaymentMethodsPage;
